// WHAT A REACT ERROR BOUNDARY CAUGHT, TOLD TO THE PLATFORM'S CRASH WITNESS. Not authored surface:
// the box restores this file from the template on every sync, and `isAgentAppWritable` refuses a
// write under `src/lib/platform/`.
//
// IT IS HERE BECAUSE THE BOUNDARY THAT CATCHES A CRASHED ROUTE IS NOT THE APP'S. TanStack wraps the
// match tree in its own `CatchBoundary` (`Matches.js:43`, unless `disableGlobalCatchBoundary`) and
// that boundary's `onCatch` is `void 0` in a production build; the per-route boundary below it
// collapses to a `SafeFragment` unless the route sets `errorComponent` (`Match.js:44`), which no
// generated app does. The app's own boundary wraps `<RouterProvider/>` from ABOVE, so it never sees
// the throw. A crashed route therefore reported NOTHING and still stamped `data-app-ready`: no crash
// lane, no repair round, its screenshot still in front of the vision judge, and the journey drive
// charging the controls it could not find to the author's key map. React's own
// `defaultOnCaughtError` is `console.error(error)` — one console line, and a console line is
// evidence in this pipeline, never a verdict.
//
// `createRoot(el, { onCaughtError })` fires for EVERY boundary in the root — TanStack's global one,
// its per-route one, the app's, any the author writes — so the platform holds this fact end to end
// and no author has to be told to report it. It is deliberately NOT paired with `onUncaughtError`:
// React's default there is `reportGlobalError`, which raises a window `error` event that the shell's
// own listener already witnesses as `window-error`, and overriding it would replace working cover.
//
// EVERY CAUGHT ERROR IS A PAGE CRASH, and that is a policy rather than an observation. `source` is
// always `error-boundary`, which `PAGE_CRASH_UI_ERROR_SOURCES` reads as "the page is gone". The root
// cannot tell a boundary around one widget from one around the whole tree, so a scoped boundary's
// catch is reported as a crash too — the route goes to the repair lane instead of the vision judge.
// That is the safe direction: an app is repaired with the real error rather than scored on a broken
// screen. If a scoped-evidence class is ever wanted (`capability-boundary` names one and has no
// producer anywhere today), it belongs HERE, keyed off the boundary instance React hands over — not
// in an authored boundary, which is exactly the arrangement this file replaces.
//
// PURE, and it imports nothing at run time, for the same reason `access.ts` beside it does: it has
// to be testable without react, without a DOM and without this app's own node_modules.

/** The report the shell's witness accepts. `index.html` installs `__onautoReportUiError` as the
 *  first script in `<head>`, before this bundle is evaluated, and owns the buffer, the caps, the
 *  noise filter and the beacon. */
interface UiErrorReport {
  source: 'error-boundary';
  name: string;
  message: string;
  stack?: string;
  componentStack?: string;
}

/**
 * TanStack's not-found signal is THROWN, and it is not a crash. `notFound()` returns a plain object
 * carrying `isNotFound: true` which a route throws to reach the nearest `notFoundComponent`, and the
 * boundary that catches it (`CatchNotFound`) is a real class boundary — so React reports it here
 * exactly as it reports a crash. Reporting it would send a page that rendered its OWN not-found
 * screen, and stamped `data-app-notfound` to say so, into the crash lane.
 *
 * Matched on the same property TanStack's own `isNotFound` reads (`router-core` `not-found.js`)
 * rather than imported, because this module imports nothing — and because the platform must keep
 * witnessing crashes in an app whose router has moved on.
 */
const isNotFoundSignal = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { isNotFound?: unknown }).isNotFound === true;

/** What was thrown, when it was not an `Error`. `String(x)` renders a plain object or a `Response` as
 *  `[object Object]` / `[object Response]` — the one description that tells a repair round nothing at
 *  all — so the constructor's name carries the identity instead. */
const nameOf = (error: unknown): string => {
  if (error instanceof Error) return error.name;
  if (typeof error !== 'object' || error === null) return typeof error;
  return error.constructor?.name ?? 'Object';
};

/**
 * React's `onCaughtError`. Reports to the shell's witness and then logs exactly what
 * `defaultOnCaughtError` would have — passing the option REPLACES that default, and a developer
 * running `pnpm dev` must not lose the console line as the price of the report.
 */
export function reportCaughtError(error: unknown, errorInfo: { componentStack?: string | undefined }): void {
  if (isNotFoundSignal(error)) return;
  const report: UiErrorReport = {
    source: 'error-boundary',
    name: nameOf(error),
    message: error instanceof Error ? error.message : String(error),
    ...(error instanceof Error && error.stack !== undefined ? { stack: error.stack } : {}),
    ...(errorInfo.componentStack !== undefined ? { componentStack: errorInfo.componentStack } : {}),
  };
  (globalThis as { __onautoReportUiError?: (report: UiErrorReport) => void }).__onautoReportUiError?.(report);
  console.error(error);
}
